import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError, setTenantContext } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);            // user object or null
  const [memberships, setMemberships] = useState([]);
  const [status, setStatus] = useState("loading");   // loading | authed | anon
  const [orgId, setOrgId] = useState(() => localStorage.getItem("orgId") || null);
  const [branchId, setBranchId] = useState(() => localStorage.getItem("branchId") || null);

  useEffect(() => {
    setTenantContext(orgId, branchId);
  }, [orgId, branchId]);

  const activeMembership = memberships.find((m) => m.organization_id === orgId) || memberships[0] || null;
  const permissions = new Set(
    user?.platform_role === "oneb_super_admin"
      ? ["*"]
      : (activeMembership?.permissions || [])
  );
  const hasPerm = (p) => permissions.has("*") || permissions.has(p);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setMemberships(data.memberships || []);
      setStatus("authed");
      // pick default org if none selected
      if (!localStorage.getItem("orgId") && data.memberships?.[0]) {
        setOrgId(data.memberships[0].organization_id);
        localStorage.setItem("orgId", data.memberships[0].organization_id);
      }
      return data;
    } catch (_) {
      setUser(null);
      setMemberships([]);
      setStatus("anon");
      return null;
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    setMemberships(data.memberships || []);
    setStatus("authed");
    if (data.memberships?.[0]) {
      setOrgId(data.memberships[0].organization_id);
      localStorage.setItem("orgId", data.memberships[0].organization_id);
    }
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) {}
    setUser(null);
    setMemberships([]);
    setStatus("anon");
    localStorage.removeItem("orgId");
    localStorage.removeItem("branchId");
    setOrgId(null); setBranchId(null);
  };

  const selectOrg = (id) => {
    setOrgId(id);
    localStorage.setItem("orgId", id);
    setBranchId(null);
    localStorage.removeItem("branchId");
  };
  const selectBranch = (id) => {
    setBranchId(id);
    if (id) localStorage.setItem("branchId", id); else localStorage.removeItem("branchId");
  };

  return (
    <AuthContext.Provider
      value={{
        user, memberships, status, orgId, branchId,
        activeMembership, hasPerm, permissions,
        login, logout, refresh, selectOrg, selectBranch,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
export { formatApiError };
